import React from "react";
import ROICalculator from "../components/ROICalculator";

/** ROI summary table — content from client reference (branded v3). */
const roiTableRows = [
  {
    facility: "50 beds",
    team: "1 admin + 2 dept heads",
    manualHrsWeek: "~60",
    hoursSavedWeek: "~24",
    monthlySavings: "$4,800",
    monthlyPrice: "$300",
    paybackDays: "~1.9",
    isEnterprise: false,
  },
  {
    facility: "80 beds",
    team: "1 admin + 3 dept heads",
    manualHrsWeek: "~96",
    hoursSavedWeek: "~38",
    monthlySavings: "$7,680",
    monthlyPrice: "$575",
    paybackDays: "~2.2",
    isEnterprise: false,
  },
  {
    facility: "125 beds",
    team: "1 admin + 6 dept heads",
    manualHrsWeek: "~150",
    hoursSavedWeek: "~60",
    monthlySavings: "$12,000",
    monthlyPrice: "$800",
    paybackDays: "~2.0",
    isEnterprise: false,
  },
  {
    facility: "200 beds",
    team: "2 admins + 9 dept heads",
    manualHrsWeek: "~240",
    hoursSavedWeek: "~96",
    monthlySavings: "$19,200",
    monthlyPrice: "$1,025",
    paybackDays: "~1.6",
    isEnterprise: false,
  },
  {
    facility: "300 beds",
    facilitySub: "Enterprise",
    team: "3 admins + 14 dept heads",
    manualHrsWeek: "~360",
    hoursSavedWeek: "~144",
    monthlySavings: "$28,800",
    monthlyPrice: "Upon request",
    paybackDays: "~2.5",
    isEnterprise: true,
  },
] as const;

const TABLE_GRID =
  "grid grid-cols-[minmax(72px,0.95fr)_minmax(120px,1.4fr)_minmax(72px,1fr)_minmax(88px,1.1fr)_minmax(96px,1.15fr)_minmax(72px,1fr)_minmax(64px,0.9fr)]";

const ROI: React.FC = () => {
  return (
    <section className="max-w-7xl mx-auto py-12 my-12 px-4">
      <h2 className="text-3xl font-bold text-blue-800 mb-4">
        RoomRounds ROI summary for 50, 80, 125, 200, and enterprise-scale
        skilled nursing facilities, with data sources and key assumptions.
      </h2>

      <div className="rounded-xl bg-slate-100 p-6">
        <div className="mb-1.5 text-[22px] font-medium leading-tight text-gray-900">
          ROI summary (estimated) — example skilled nursing facilities
        </div>
        <p className="mb-[18px] text-sm leading-normal text-gray-500">
          Estimates show how quickly RoomRounds can pay for itself by reducing
          manual rounds and ops-coordination time. Results vary by facility
          workflow, staffing model, and adoption.
        </p>

        {/* <div className="mb-10 flex justify-center">
          <button
            type="button"
            onClick={() => setIsCalculatorOpen(true)}
            className="inline-flex items-center justify-center rounded-xl bg-blue-700 px-6 py-3 font-semibold text-white shadow transition hover:bg-blue-800"
          >
            Open ROI Calculator
          </button>
        </div> */}

        {/* <ROICalculator /> */}

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <div className="min-w-[720px]">
            <div
              className={`${TABLE_GRID} bg-blue-800 px-4 py-[13px] text-xs font-medium leading-snug text-white`}
            >
              <div>Facility</div>
              <div>Team doing rounds</div>
              <div>Manual hrs/week</div>
              <div>Hours saved/week (40%)</div>
              <div>Monthly savings (est.)</div>
              <div>Monthly price</div>
              <div>Payback (days)</div>
            </div>

            {roiTableRows.map((row) => (
              <div
                key={row.facility}
                className={`${TABLE_GRID} items-center border-t border-gray-200 px-4 py-3.5 text-sm text-gray-800 ${
                  row.isEnterprise
                    ? "border-t-2 border-t-blue-800 bg-blue-50"
                    : ""
                }`}
              >
                <div className="font-medium text-blue-900">
                  {row.facility}
                  {"facilitySub" in row && row.facilitySub && (
                    <>
                      <br />
                      <span className="text-[11px] font-normal text-gray-500">
                        {row.facilitySub}
                      </span>
                    </>
                  )}
                </div>
                <div className="text-xs text-gray-600">{row.team}</div>
                <div>{row.manualHrsWeek}</div>
                <div className="font-medium text-blue-800">
                  {row.hoursSavedWeek}
                </div>
                <div className="font-medium text-green-700">
                  {row.monthlySavings}
                </div>
                <div className={row.isEnterprise ? "font-medium" : ""}>
                  {row.monthlyPrice}
                </div>
                <div className="font-medium">{row.paybackDays}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-2.5 px-0.5 text-xs italic leading-relaxed text-gray-400">
          *Based on 40% documented efficiency improvement (LeadingAge 2024).
          Conservative blended labor rate: $50/hr. Across all facility sizes,
          RoomRounds typically pays for itself in roughly 2–3 days. Enterprise
          pricing (200+ beds) is custom — contact us for a quote.
        </p>

        <div className="mt-6 grid items-stretch gap-6 md:grid-cols-2">
          <div className="flex flex-col">
            <div className="mb-2.5 text-lg font-bold text-gray-800">
              Where the data comes from
            </div>
            <div className="flex-1 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2.5 text-sm font-semibold text-gray-700">
                Data sources
              </div>
              <div className="space-y-0 text-sm leading-8 text-gray-600">
                <div>
                  <span className="font-medium text-gray-800">
                    Staffing levels:
                  </span>{" "}
                  AHCA 2023, CMS Care Compare 2024
                </div>
                <div>
                  <span className="font-medium text-gray-800">
                    Time on rounds:
                  </span>{" "}
                  CliftonLarsonAllen 2023
                </div>
                <div>
                  <span className="font-medium text-gray-800">
                    Labor cost:
                  </span>{" "}
                  Bureau of Labor Statistics 2024
                </div>
                <div>
                  <span className="font-medium text-gray-800">
                    Rounding team mix:
                  </span>{" "}
                  RoomRounds internal estimate
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="mb-2.5 text-lg font-bold text-gray-800">
              Key assumptions
            </div>
            <div className="flex-1 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-2.5 text-sm font-semibold text-gray-700">
                Inputs &amp; rates
              </div>
              <div className="space-y-0 text-sm leading-8 text-gray-600">
                <div>
                  <span className="font-medium text-gray-800">
                    Efficiency gain:
                  </span>{" "}
                  40% (LeadingAge 2024)
                </div>
                <div>
                  <span className="font-medium text-gray-800">
                    Example sizes:
                  </span>{" "}
                  50, 80, 125, 200, 300 beds
                </div>
                <div>
                  <span className="font-medium text-gray-800">
                    Module adoption:
                  </span>{" "}
                  50% of maintenance &amp; support
                </div>
                <div>
                  <span className="font-medium text-gray-800">
                    Avg labor rate:
                  </span>{" "}
                  $50/hr ·{" "}
                  <span className="font-medium text-gray-800">Roles:</span>{" "}
                  admins, dept heads
                </div>
              </div>
            </div>
          </div>
        </div>
        <ROICalculator />
      </div>

      {/* Modal calculator — replaced by inline client calculator above
      {isCalculatorOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="RoomRounds ROI Calculator"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsCalculatorOpen(false);
          }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div ref={dialogRef} className="relative z-10 w-full max-w-5xl">
            <div className="overflow-hidden rounded-3xl">
              <ROICalculator onClose={() => setIsCalculatorOpen(false)} />
            </div>
          </div>
        </div>
      )}
      */}
    </section>
  );
};

export default ROI;
